'use client';

import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PRICING_TIERS, BillingType, PricingTierId } from '@/types';
import { Button } from './Button';

/**
 * Feature keys that describe the monthly contact volume. They are rendered as
 * the headline number on each card (from tier.vcCount), never as a bullet.
 */
const VOLUME_FEATURE_PREFIX = 'automatedSending';

/**
 * Feature keys that genuinely differ between tiers and stay inside the card.
 *
 * Currently EMPTY: monthly contact volume is the only real difference between
 * plans. 'prioritySupport' used to live here and was removed — it was an
 * operational promise with nothing behind it in code. Do not add a key here
 * unless the tier genuinely gates the behaviour.
 */
const TIER_EXCLUSIVE_FEATURES: string[] = [];

/**
 * Capabilities shipped by every tier — derived from PRICING_TIERS (types/index.ts
 * is authoritative) so this stays correct if the tier definitions change. Shown
 * once beneath the cards instead of being repeated identically in all three.
 */
const SHARED_FEATURES: string[] = PRICING_TIERS.reduce<string[]>((shared, tier, index) => {
  const relevant = tier.features.filter(
    (feature) =>
      !feature.startsWith(VOLUME_FEATURE_PREFIX) && !TIER_EXCLUSIVE_FEATURES.includes(feature)
  );
  return index === 0 ? relevant : shared.filter((feature) => relevant.includes(feature));
}, []);

interface SubscriptionTierSelectorProps {
  onSelect: (tier: PricingTierId, billingType: BillingType) => void;
  defaultTier?: PricingTierId;
  defaultBilling?: BillingType;
}

export const SubscriptionTierSelector: React.FC<SubscriptionTierSelectorProps> = ({
  onSelect,
  defaultTier = 'professional',
  defaultBilling = 'monthly'
}) => {
  const { t } = useTranslation();
  const [billingType, setBillingType] = useState<BillingType>(defaultBilling);
  const [selectedTier, setSelectedTier] = useState<PricingTierId>(defaultTier);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // The parent can set the plan after mount (from ?tier=&billing= or resubscribe data).
  useEffect(() => setSelectedTier(defaultTier), [defaultTier]);
  useEffect(() => setBillingType(defaultBilling), [defaultBilling]);

  const getPrice = (tier: typeof PRICING_TIERS[0]) => {
    if (billingType === 'yearly') {
      return tier.priceYearly / 12; // Show monthly equivalent
    }
    return tier.priceMonthly;
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(0)}`;
  };

  const handleSelectTier = (tierId: PricingTierId) => {
    setSelectedTier(tierId);
    onSelect(tierId, billingType);
  };

  const handleBillingChange = (newBillingType: BillingType) => {
    setBillingType(newBillingType);
    onSelect(selectedTier, newBillingType);
  };

  // Don't render until mounted to avoid hydration mismatch
  if (!mounted) {
    return <div className="space-y-6">
      <div className="h-12 bg-gray-100 rounded-full w-64 mx-auto"></div>
      <div className="grid md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-64 bg-gray-100 rounded-xl border border-gray-200"></div>
        ))}
      </div>
    </div>;
  }

  return (
    <div className="space-y-6">
      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4">
        <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
          <button
            type="button"
            onClick={() => handleBillingChange('monthly')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              billingType === 'monthly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('pageContent.pricing.billingToggle.monthly')}
          </button>
          <button
            type="button"
            onClick={() => handleBillingChange('yearly')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all relative ${
              billingType === 'yearly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('pageContent.pricing.billingToggle.yearly')}
            <span className="absolute -top-2 -right-2 bg-green-700 text-white text-xs px-1.5 py-0.5 rounded-full">
              {t('pageContent.pricing.billingToggle.savePercent')}
            </span>
          </button>
        </div>
      </div>

      {/* Tier Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {PRICING_TIERS.map((tier) => (
          <button
            key={tier.id}
            type="button"
            onClick={() => handleSelectTier(tier.id)}
            className={`relative bg-white rounded-xl p-6 text-left transition-all ${
              selectedTier === tier.id
                ? 'border-2 border-primary-500 ring-4 ring-primary-100 shadow-lg'
                : 'border border-gray-200 hover:border-primary-300 hover:shadow-md'
            }`}
          >
            {tier.highlighted && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                {t(`pageContent.pricing.tiers.${tier.id}.badge`)}
              </div>
            )}
            
            <div className="space-y-3">
              <h3 className="text-lg font-bold text-gray-900">
                {t(`pageContent.pricing.tiers.${tier.id}.name`)}
              </h3>
              
              <div>
                <span className="text-3xl font-bold text-gray-900">
                  {formatPrice(getPrice(tier))}
                </span>
                <span className="text-gray-500 text-sm">/mo</span>
                {billingType === 'yearly' && (
                  <p className="text-xs text-green-600 mt-1">
                    {t('pageContent.pricing.selector.billedAnnually', {
                      price: formatPrice(tier.priceYearly),
                    })}
                  </p>
                )}
              </div>

              {/* The one real difference between tiers: monthly contact volume */}
              <div className="bg-primary-50 rounded-lg px-3 py-3 text-center">
                <div className="text-2xl font-bold text-primary-700 leading-none">
                  {tier.vcCount}
                </div>
                <div className="text-xs text-primary-700 mt-1" suppressHydrationWarning>
                  {t('pageContent.pricing.selector.contactsPerMonth')}
                </div>
              </div>

              {/* Tier-exclusive perks only — shared capabilities are listed once below */}
              {tier.features.filter((featureKey) => TIER_EXCLUSIVE_FEATURES.includes(featureKey))
                .length > 0 && (
                <ul className="space-y-2">
                  {tier.features
                    .filter((featureKey) => TIER_EXCLUSIVE_FEATURES.includes(featureKey))
                    .map((featureKey) => (
                      <li key={featureKey} className="flex items-start text-xs">
                        <div className="rounded-full bg-green-100 p-0.5 mr-2 flex-shrink-0 mt-0.5">
                          <Check className="w-2.5 h-2.5 text-green-600" />
                        </div>
                        <span className="text-gray-600" suppressHydrationWarning>
                          {t(`pageContent.pricing.tiers.${tier.id}.features.${featureKey}`)}
                        </span>
                      </li>
                    ))}
                </ul>
              )}

              {selectedTier === tier.id && (
                <div className="absolute top-3 right-3">
                  <div className="rounded-full bg-primary-500 p-1">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Shared value, stated once instead of repeated in every card */}
      <div className="rounded-xl border border-gray-200 bg-gray-50 px-5 py-4">
        <p
          className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3"
          suppressHydrationWarning
        >
          {t('pageContent.pricing.selector.includedInEveryPlan')}
        </p>
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
          {SHARED_FEATURES.map((featureKey) => (
            <li key={featureKey} className="flex items-start text-xs">
              <div className="rounded-full bg-green-100 p-0.5 mr-2 flex-shrink-0 mt-0.5">
                <Check className="w-2.5 h-2.5 text-green-600" />
              </div>
              <span className="text-gray-600" suppressHydrationWarning>
                {t(`pageContent.pricing.tiers.starter.features.${featureKey}`)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
